import { createSelector } from '@reduxjs/toolkit';
import { inboxContactMatchesUnreadFilter } from '../utils/inboxThreadFlags';

// Base selectors (these are already memoized by Redux)
const selectInboxMessage = (state) => state.inboxMessage;
const selectInboxRecent = (state) => state.inboxRecent;
const selectInboxInfo = (state) => state.inboxInfo;
const selectUserDetails = (state) => state.userDetails;
const selectInbox = (state) => state.inbox;
const selectInboxAll = (state) => state.inboxAll;
const selectInboxSettings = (state) => state.inboxSettings;
const selectContacts = (state) => state.contacts;
const selectContactGroups = (state) => state.contactGroups;
const selectLabels = (state) => state.labels;
const selectTemplate = (state) => state.template;
const selectTemplateCreation = (state) => state.templateCreation;
const selectDashboard = (state) => state.dashboard;
const selectAuth = (state) => state.auth;
const selectLoading = (state) => state.loading;
const selectAiAgentsState = (state) => state.aiAgents;

// Memoized selectors for inbox message
export const selectMessages = createSelector(
  [selectInboxMessage],
  (inboxMessage) => inboxMessage.messages
);

export const selectInboxMessageLoading = createSelector(
  [selectInboxMessage],
  (inboxMessage) => inboxMessage.loading
);

export const selectInboxMessageError = createSelector(
  [selectInboxMessage],
  (inboxMessage) => inboxMessage.error
);

export const selectContactNumber = createSelector(
  [selectMessages],
  (messages) => messages?.contactNumber
);

export const selectContactID = createSelector(
  [selectMessages],
  (messages) => messages?.contactID
);

export const selectChats = createSelector(
  [selectMessages],
  (messages) => messages?.chats || []
);

// Memoized selectors for inbox recent
export const selectInboxRecentData = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.data
);

export const selectInboxRecentLoading = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.loading
);

export const selectInboxRecentError = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.error
);

export const selectInboxRecentInitialized = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.initialized ?? false
);

// Stable scalar selectors — avoid inline state => ... lambdas in useSelector calls
export const selectInboxRecentPagination = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.pagination || {}
);

export const selectInboxRecentIsLoadingMore = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.isLoadingMore || false
);

export const selectInboxRecentLazyTabsLoaded = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.lazyTabsLoaded || {}
);

export const selectInboxRecentSearchResults = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.searchResults ?? null
);

export const selectInboxRecentIsSearching = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.isSearching || false
);

export const selectInboxRecentCounts = createSelector(
  [selectInboxRecent],
  (inboxRecent) => {
    const data = inboxRecent.data || {};
    const pagination = inboxRecent.pagination || {};
    const stored = inboxRecent.counts || {};

    // Derive each count directly from the live data array so that socket-event
    // mutations to state.data.X are reflected immediately without depending on
    // updateAllCounts being called with exactly the right timing.
    const derive = (category) => {
      const arr = data[category];
      const len = Array.isArray(arr) ? arr.length : 0;
      const hasMore = pagination[category]?.hasMore ?? false;
      if (hasMore) {
        // Server has more pages — show the larger of server total vs local length
        return Math.max(pagination[category]?.totalCount ?? stored[category] ?? 0, len);
      }
      // All data is loaded locally — local length is authoritative for real-time accuracy.
      // Fall back to the last server count when the array is empty but hasn't been fetched yet.
      return len > 0 ? len : (stored[category] ?? 0);
    };

    // Groups' badge is unread MESSAGES, not "how many groups" (bug fix, 2026-09-26 follow-up) --
    // unlike every other category, so it can't reuse `derive`'s array-length logic. Each hydrated
    // group card already carries its own `receivedCount` (same field the Groups/All list rows
    // read for their own unread state); sum it when local data is available (live-accurate),
    // else fall back to the server-computed total (inboxController.js's groupsUnreadCount) the
    // same way `derive` falls back to `stored` for every other category.
    const deriveGroupsUnread = () => {
      const arr = data['Groups'];
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.reduce((sum, g) => sum + (Number(g?.receivedCount) || 0), 0);
      }
      return stored['Groups'] ?? 0;
    };

    return {
      All:          derive('All'),
      You:          derive('You'),
      Active:       derive('Active'),
      Unassigned:   derive('Unassigned'),
      Assigned:     derive('Assigned'),
      Closed:       derive('Closed'),
      Blocked:      derive('Blocked'),
      Groups:       deriveGroupsUnread(),
    };
  }
);

// Total unread-by-team count for the current workspace's inbox, for the browser-tab
// badge/title (see useUnreadTabBadge.js) — "All" is the same unified list the inbox's
// own "Show > Unread" filter counts against (inboxContactMatchesUnreadFilter), so the
// tab badge always agrees with what filtering to Unread shows in-app. Deliberately not
// aggregated across other workspaces — the client only ever holds the currently open
// workspace's inbox state.
export const selectTotalUnreadInboxCount = createSelector(
  [selectInboxRecentData],
  (data) => {
    const contacts = data?.All;
    if (!Array.isArray(contacts) || contacts.length === 0) return 0;
    let count = 0;
    for (const contact of contacts) {
      if (inboxContactMatchesUnreadFilter(contact)) count++;
    }
    return count;
  }
);

// Return the raw arrays — count normalisation happens in the reducer (inboxRecentSlice)
// so we never create new object references here on every selector call.
export const selectTeamMembersFromInbox = createSelector(
  [selectInboxRecentData],
  (data) => data.TeamMembers || []
);

export const selectTeamsFromInbox = createSelector(
  [selectInboxRecentData],
  (data) => data.Teams || []
);

// Memoized selectors for inbox info
export const selectContactInfo = createSelector(
  [selectInboxInfo],
  (inboxInfo) => inboxInfo.contactInfo
);

export const selectInboxInfoLabels = createSelector(
  [selectInboxInfo],
  (inboxInfo) => inboxInfo.labels
);

export const selectInboxInfoLoading = createSelector(
  [selectInboxInfo],
  (inboxInfo) => inboxInfo.isLoading
);

export const selectInboxInfoError = createSelector(
  [selectInboxInfo],
  (inboxInfo) => inboxInfo.error
);

// Memoized selectors for user details
export const selectUserDetailsData = createSelector(
  [selectUserDetails],
  (userDetails) => userDetails.userDetails
);

export const selectUserPermissions = createSelector(
  [selectUserDetails],
  (userDetails) => userDetails.userPermissions
);

export const selectUserPermissionsPermissions = createSelector(
  [selectUserPermissions],
  (permissions) => permissions?.permissions || {}
);

export const selectTeamPermissions = createSelector(
  [selectUserPermissionsPermissions],
  (permissions) => permissions.Team || {}
);

export const selectContactPermissions = createSelector(
  [selectUserPermissionsPermissions],
  (permissions) => permissions.Contact || {}
);

export const selectDashboardPermissions = createSelector(
  [selectUserPermissionsPermissions],
  (permissions) => permissions.Dashboard || {}
);

export const selectTeamMemberPermissions = createSelector(
  [selectUserPermissionsPermissions],
  (permissions) => permissions.TeamMember || {}
);

export const selectLabelPermissions = createSelector(
  [selectUserPermissionsPermissions],
  (permissions) => permissions.Label || {}
);

// Memoized selectors for inbox
export const selectShowInboxSettings = createSelector(
  [selectInbox],
  (inbox) => inbox.showInboxSettings
);

export const selectShowChatInfo = createSelector(
  [selectInbox],
  (inbox) => inbox.showChatInfo
);

export const selectSelectedSettings = createSelector(
  [selectInbox],
  (inbox) => inbox.selectedSettings
);

export const selectSelectedTeamMember = createSelector(
  [selectInbox],
  (inbox) => inbox.selectedTeamMember
);

export const selectSelectedTeam = createSelector(
  [selectInbox],
  (inbox) => inbox.selectedTeam
);

export const selectSelectedInboxTab = createSelector(
  [selectInbox],
  (inbox) => inbox.selectedInboxTab
);

// Memoized selectors for inbox all
export const selectSelectedContactID = createSelector(
  [selectInboxAll],
  (inboxAll) => inboxAll.selectedContactID
);

// Helper function to check if a contact exists in inbox data
const checkContactExistsInInboxData = (inboxData, contactId) => {
  if (!contactId || !inboxData) return false;
  
  const contactIdStr = String(contactId);
  
  // Check all categories in inbox data
  const categories = ['All', 'You', 'Active', 'Unassigned', 'Assigned', 'Closed', 'Blocked', 'Pinned', 'Groups'];
  for (const category of categories) {
    if (Array.isArray(inboxData[category])) {
      const found = inboxData[category].some(
        (contact) => String(contact.contactId || contact.contactID) === contactIdStr
      );
      if (found) return true;
    }
  }
  
  // Check TeamMembers
  if (Array.isArray(inboxData.TeamMembers)) {
    for (const member of inboxData.TeamMembers) {
      if (Array.isArray(member.data)) {
        const found = member.data.some(
          (contact) => String(contact.contactId || contact.contactID) === contactIdStr
        );
        if (found) return true;
      }
    }
  }
  
  // Check Teams
  if (Array.isArray(inboxData.Teams)) {
    for (const team of inboxData.Teams) {
      if (Array.isArray(team.data)) {
        const found = team.data.some(
          (contact) => String(contact.contactId || contact.contactID) === contactIdStr
        );
        if (found) return true;
      }
    }
  }
  
  return false;
};

// Selector to check if the selected contact exists in inbox data
export const selectContactExistsInInbox = createSelector(
  [selectInboxRecentData, selectSelectedContactID],
  (inboxData, contactId) => checkContactExistsInInboxData(inboxData, contactId)
);

// Export the helper function for use in components
export { checkContactExistsInInboxData };

export const selectActiveMembers = createSelector(
  [selectInboxAll],
  (inboxAll) => inboxAll.activeMembers
);

// See inboxAllSlice.js's activeMembersLoaded comment — tells apart "genuinely
// nobody else online" from "haven't gotten a successful response yet".
export const selectActiveMembersLoaded = createSelector(
  [selectInboxAll],
  (inboxAll) => inboxAll.activeMembersLoaded
);

// Per-stage session cache for Lifecycle-filtered chat lists (agent clicked a stage in
// InboxSettings.jsx's Lifecycle rail) — { [stageId]: { data, count, hasMore, ... } },
// same inboxRecent slice as All/You/Closed etc, see fetchLifecycleTabInbox.
export const selectLifecycleByStage = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.lifecycleByStage || {}
);
export const selectLifecycleTabLoading = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.lifecycleTabLoading || false
);
export const selectSelectedLifecycleStageId = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.selectedLifecycleStageId || null
);

// Per-agent session cache for AI Takeover-filtered chat lists (agent clicked an AI
// agent in InboxSettings.jsx's AI Agents rail) — { [agentId]: { data, count, hasMore,
// ... } }, same inboxRecent slice as Lifecycle above, see fetchAgentTabInbox.
export const selectAgentByAgentId = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.agentByAgentId || {}
);
export const selectAgentTabLoading = createSelector(
  [selectInboxRecent],
  (inboxRecent) => inboxRecent.agentTabLoading || false
);

// Memoized selectors for inbox settings
export const selectTeamMembersWithNames = createSelector(
  [selectInboxSettings],
  (inboxSettings) => inboxSettings.teamMembers || []
);

export const selectTeamsWithNames = createSelector(
  [selectInboxSettings],
  (inboxSettings) => inboxSettings.teams || []
);

// Current workspace's AI agents (AgentModel) — same roster role for AI Takeover
// assignment as selectTeamMembersWithNames/selectTeamsWithNames above, just backed
// by aiAgentsSlice instead of inboxSettingsSlice (see aiAgentsSlice.js).
export const selectAiAgents = createSelector(
  [selectAiAgentsState],
  (aiAgents) => aiAgents?.agents || []
);

export const selectAiAgentsLoading = createSelector(
  [selectAiAgentsState],
  (aiAgents) => aiAgents?.loading || false
);

// { [agentId]: count } — fetched upfront with the roster, kept live via
// adjustAgentCount. Survives a page refresh, unlike inboxRecent.agentByAgentId
// (session-only, populated by clicking that agent's rail row) — see aiAgentsSlice.js.
export const selectAiAgentCounts = createSelector(
  [selectAiAgentsState],
  (aiAgents) => aiAgents?.counts || {}
);

export const selectAssignStatus = createSelector(
  [selectInboxSettings],
  (inboxSettings) => inboxSettings.assignStatus
);

// Memoized selectors for contacts
export const selectContactsData = createSelector(
  [selectContacts],
  (contacts) => contacts.contacts
);

export const selectSelectedContacts = createSelector(
  [selectContacts],
  (contacts) => contacts.selectedContacts
);

export const selectContactsLoading = createSelector(
  [selectContacts],
  (contacts) => contacts.loading
);

export const selectContactsSearchTerm = createSelector(
  [selectContacts],
  (contacts) => contacts.searchTerm
);

export const selectContactsStatusFilter = createSelector(
  [selectContacts],
  (contacts) => contacts.statusFilter
);

export const selectContactsPagination = createSelector(
  [selectContacts],
  (contacts) => contacts.pagination
);

export const selectContactsSortField = createSelector(
  [selectContacts],
  (contacts) => contacts.sortField
);

export const selectContactsSortDirection = createSelector(
  [selectContacts],
  (contacts) => contacts.sortDirection
);

// Memoized selectors for contact groups
export const selectGroups = createSelector(
  [selectContactGroups],
  (contactGroups) => contactGroups.groups
);

// Memoized selectors for labels
export const selectLabelsData = createSelector(
  [selectLabels],
  (labels) => labels.labels
);

export const selectLabelFilter = createSelector(
  [selectLabels],
  (labels) => labels.labelFilter
);

// Memoized selectors for template
export const selectSelectedTemplate = createSelector(
  [selectTemplate],
  (template) => template.selectedTemplate
);

export const selectTemplates = createSelector(
  [selectTemplate],
  (template) => template.templates
);

export const selectTemplateLoading = createSelector(
  [selectTemplate],
  (template) => template.loading
);

export const selectTemplateError = createSelector(
  [selectTemplate],
  (template) => template.error
);

export const selectTemplateStatus = createSelector(
  [selectTemplate],
  (template) => template.status
);

export const selectTemplateUploadMediaIDStatus = createSelector(
  [selectTemplate],
  (template) => template.uploadMediaIDStatus
);

export const selectTemplateUploadMediaIDResult = createSelector(
  [selectTemplate],
  (template) => template.uploadMediaIDResult
);

export const selectTemplateUploadMediaIDError = createSelector(
  [selectTemplate],
  (template) => template.uploadMediaIDError
);

// Memoized selectors for template creation
export const selectTemplateCreationData = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation
);

export const selectActiveStep = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.activeStep
);

export const selectSteps = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.steps
);

export const selectFieldsFlag = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.fieldsFlag
);

export const selectFieldsErrorMsg = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.fieldsErrorMsg
);

export const selectTemplateCreationStatus = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.status
);

export const selectTemplateCreationError = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.error
);

export const selectShouldCreate = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.shouldCreate
);

export const selectSelectedCategory = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.selectedCategory
);

export const selectButtons = createSelector(
  [selectTemplateCreation],
  (templateCreation) => templateCreation.buttons
);

// Memoized selectors for dashboard
export const selectAccount = createSelector(
  [selectDashboard],
  (dashboard) => dashboard.account
);

export const selectDashboardError = createSelector(
  [selectDashboard],
  (dashboard) => dashboard.error
);

// Memoized selectors for auth
export const selectIsAuthenticated = createSelector(
  [selectAuth],
  (auth) => auth.isAuthenticated
);

export const selectAuthLoading = createSelector(
  [selectAuth],
  (auth) => auth.isLoading
);

// Memoized selectors for loading
export const selectIsPageLoading = createSelector(
  [selectLoading],
  (loading) => loading.isPageLoading
);

// Complex memoized selectors that combine multiple state pieces
export const selectUnreadIncomingMessages = createSelector(
  [selectChats],
  (chats) => chats.filter(chat => 
    (chat.status === "received" || chat.status === "delivered") && !chat.isFromMe
  )
);

export const selectContactSelected = createSelector(
  [selectContactID],
  (contactID) => Boolean(contactID)
);

export const selectFilteredContacts = createSelector(
  [selectContactsData, selectContactsSearchTerm, selectContactsStatusFilter],
  (contacts, searchTerm, statusFilter) => {
    if (!contacts) return [];
    
    let filtered = contacts;
    
    if (searchTerm) {
      filtered = filtered.filter(contact =>
        contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phoneNumber?.includes(searchTerm)
      );
    }
    
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(contact => contact.status === statusFilter);
    }
    
    return filtered;
  }
);

// ---- Live calls (workspace-wide "Calls" sidebar section) ----

const selectLiveCallsById = (state) => state.liveCalls?.byId;

export const selectLiveCallsHydrated = (state) => state.liveCalls?.hydrated || false;

// Ringing first (most urgent), then connected — newest of each group first.
// Stable sort within a status: Object.values preserves insertion order for
// equal keys, and JS sort is stable, so ties just fall back to that order.
export const selectLiveCallsList = createSelector([selectLiveCallsById], (byId) => {
  const calls = Object.values(byId || {});
  const rank = { ringing: 0, connecting: 0, connected: 1 };
  return calls.sort((a, b) => {
    const r = (rank[a.status] ?? 2) - (rank[b.status] ?? 2);
    if (r !== 0) return r;
    return new Date(b.startedAt || 0) - new Date(a.startedAt || 0);
  });
});

export const selectLiveCallsCount = createSelector([selectLiveCallsById], (byId) => Object.keys(byId || {}).length);

// callIds this tab actually has an offerSdp for (see callSlice.js) — a call
// can be visible in selectLiveCallsList (workspace-wide) without being
// answerable here, e.g. a ring-group call this agent isn't a member of.
export const selectAnswerableCallIds = (state) => state.calls?.answerableCallIds || [];
